import React, { useEffect, useState, useCallback } from "react";
import { MdOutlineChevronRight } from "react-icons/md";
import { FaSearch } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import CreateCompanyFooter from "../components/createcompanyfooter/CreateCompanyFooter";
import { getApiBaseUrl } from "../apiBaseUrlFinder";
import Icon from "@mdi/react";
import { mdiLinkedin } from "@mdi/js";

const ViewAllManagers = () => {
  const location = useLocation();
  const [companyData, setCompanyData] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const companyId = queryParams.get("id");
    const companyName = queryParams.get("name");

    if (companyId && companyName) {
      const fetchCompanyData = async () => {
        try {
          const params = new URLSearchParams({
            company_id: companyId,
            company_name: companyName, // "S&P Global" → S%26P+Global
          });
          const response = await fetch(
            `${getApiBaseUrl()}/company_profile?${params.toString()}`
          );
          if (!response.ok) throw new Error("Failed to fetch data");
          const data = await response.json();
          setCompanyData(data);
        } catch (err) {
          console.error("Error fetching company data:", err);
        }
      };

      fetchCompanyData();
    }
  }, [location.search]);

  const [managers, setManagers] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [lastKey, setLastKey] = useState(null);

  // Search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchLastKey, setSearchLastKey] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(true);
  const [selectedManager, setSelectedManager] = useState(null);
  const [suggestionsHasMore, setSuggestionsHasMore] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const fetchManagers = useCallback(
    async (append = false) => {
      if (!companyData?.company_profile?.company_name) return;

      try {
        const name = companyData?.company_profile?.company_name ?? "";
        const params = new URLSearchParams({ company_name: name }); // S&P Global -> S%26P+Global
        let url = `${getApiBaseUrl()}/company_managers?${params.toString()}`;

        if (append && lastKey)
          url += `&last_key=${encodeURIComponent(lastKey)}`;

        const res = await fetch(url);
        const data = await res.json();

        if (Array.isArray(data.managers_list)) {
          const validManagers = data.managers_list.filter(Boolean);

          setManagers((prev) =>
            append ? [...prev, ...validManagers] : validManagers
          );

          setLastKey(data.last_key || null);
          setHasMore(!!data.last_key);
        }
      } catch (error) {
        console.error("Error fetching managers:", error);
      }
    },
    [companyData?.company_profile?.company_name, lastKey]
  );

  // Separate function for initial fetch to avoid circular dependency
  const fetchInitialManagers = useCallback(async () => {
    if (!companyData?.company_profile?.company_name) return;

    try {
      const name = companyData?.company_profile?.company_name ?? "";
      const params = new URLSearchParams({ company_name: name });
      const url = `${getApiBaseUrl()}/company_managers?${params.toString()}`;

      const res = await fetch(url);
      const data = await res.json();

      if (Array.isArray(data.managers_list)) {
        const validManagers = data.managers_list.filter(Boolean);
        setManagers(validManagers);
        setLastKey(data.last_key || null);
        setHasMore(!!data.last_key);
      }
    } catch (error) {
      console.error("Error fetching initial managers:", error);
    }
  }, [companyData?.company_profile?.company_name]);

  const handleViewMore = () => {
    fetchManagers(true); // append = true
  };

  // Search functionality
  const searchManagers = useCallback(
    async (query, append = false) => {
      if (!companyData?.company_profile?.company_name || !query.trim()) {
        setSearchResults([]);
        return;
      }

      try {
        setIsSearching(true);
        const companyName = companyData.company_profile.company_name;
        const params = new URLSearchParams({
          company_name: companyName,
          manager_name: query.trim(),
        });

        let url = `${getApiBaseUrl()}/get_company_managers?${params.toString()}`;

        if (append && searchLastKey) {
          // Ensure last_key is properly formatted as JSON string
          const lastKeyValue =
            typeof searchLastKey === "string"
              ? searchLastKey
              : JSON.stringify(searchLastKey);
          url += `&last_key=${encodeURIComponent(lastKeyValue)}`;
        }

        const response = await fetch(url);
        const data = await response.json();

        if (Array.isArray(data.managers)) {
          const validManagers = data.managers.filter(Boolean);

          setSearchResults((prev) =>
            append ? [...prev, ...validManagers] : validManagers
          );

          setSearchLastKey(data.last_key || null);
          setSearchHasMore(!!data.last_key);
        }
      } catch (error) {
        console.error("Error searching managers:", error);
      } finally {
        setIsSearching(false);
      }
    },
    [companyData?.company_profile?.company_name, searchLastKey]
  );

  // Fetch search suggestions for dropdown
  const fetchSearchSuggestions = useCallback(
    async (query, append = false) => {
      console.log("fetchSearchSuggestions called with:", query);
      console.log("companyData:", companyData?.company_profile?.company_name);

      if (!companyData?.company_profile?.company_name || !query.trim()) {
        console.log("Missing company data or empty query");
        setSearchSuggestions([]);
        setShowDropdown(false);
        setIsLoadingSuggestions(false);
        return;
      }

      try {
        setIsLoadingSuggestions(true);
        const companyName = companyData.company_profile.company_name;
        const params = new URLSearchParams({
          company_name: companyName,
          manager_name: query.trim(),
        });

        let url = `${getApiBaseUrl()}/get_company_managers?${params.toString()}`;

        if (append && searchLastKey) {
          // Ensure last_key is properly formatted as JSON string
          const lastKeyValue =
            typeof searchLastKey === "string"
              ? searchLastKey
              : JSON.stringify(searchLastKey);
          url += `&last_key=${encodeURIComponent(lastKeyValue)}`;
        }

        console.log("Fetching from URL:", url);
        const response = await fetch(url);
        const data = await response.json();
        console.log("API Response:", data);

        if (Array.isArray(data.managers)) {
          const validManagers = data.managers.filter(Boolean);
          console.log("Found managers:", validManagers);

          if (append) {
            setSearchSuggestions((prev) => [...prev, ...validManagers]);
          } else {
            setSearchSuggestions(validManagers.slice(0, 5)); // Show only first 5 suggestions
          }

          setSearchLastKey(data.last_key || null);
          setSuggestionsHasMore(!!data.last_key);
          console.log("Setting showDropdown to true");
          setShowDropdown(true);
        } else {
          console.log("No managers array in response:", data);
          setSearchSuggestions([]);
          setShowDropdown(false);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSearchSuggestions([]);
        setShowDropdown(false);
      } finally {
        setIsLoadingSuggestions(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyData?.company_profile?.company_name]
  );

  // Immediate search for suggestions (no debounce)
  useEffect(() => {
    console.log("Search triggered for:", searchQuery);
    if (searchQuery.trim()) {
      fetchSearchSuggestions(searchQuery);
    } else {
      setSearchSuggestions([]);
      setShowDropdown(false);
      setSearchResults([]);
      setSearchLastKey(null);
      setSearchHasMore(false);
      setIsLoadingSuggestions(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleSearchMore = () => {
    searchManagers(searchQuery, true);
  };

  const handleSuggestionsViewMore = () => {
    fetchSearchSuggestions(searchQuery, true);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    console.log("Search input changed to:", value);
    setSearchQuery(value);
    setSelectedManager(null);
    setSuggestionsHasMore(false);
    setSearchLastKey(null);
    setIsLoadingSuggestions(false);
  };

  const handleSuggestionClick = (manager) => {
    // Directly navigate to manager profile without changing table data
    const managerName = encodeURIComponent(manager.manager_full_name);
    const managerId = manager.manager_id;
    window.location.href = `/manager?name=${managerName}&id=${managerId}`;
  };

  const handleInputFocus = () => {
    if (searchQuery.trim()) {
      // If there are already suggestions, show them
      // if (searchSuggestions.length > 0) {
      //   setShowDropdown(true);
      // } else {
      //   // If no suggestions yet, trigger search
      //   fetchSearchSuggestions(searchQuery);
      // }
    }
  };

  const handleInputBlur = (e) => {
    // Check if the blur is going to a suggestion item
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest(".search-dropdown")) {
      return;
    }

    // Delay hiding dropdown to allow clicks on suggestions
    setShowDropdown(false);
  };

  useEffect(() => {
    if (companyData?.company_profile?.company_name) {
      setManagers([]); // reset on company change
      setLastKey(null); // reset lastKey
      fetchInitialManagers(); // fetch initial data
    }
  }, [companyData?.company_profile?.company_name, fetchInitialManagers]);

  return (
    <div>
      {/* <LinkNavbar /> */}
      <div className="container">
        <div className="row pt-lg-5 pt-md-4 pt-3">
          <div className="col-lg-6 col-md-6 col-12">
            <div className="d-flex align-items-end">
              <h3 className="rating_review_h3 mb-0">
                {companyData?.company_profile?.company_rating}
              </h3>
              <h6 className="add_review_rate_h3 ps-2">/ 5</h6>
            </div>

            <p className="company_profile_rating_p pt-2">
              {" "}
              <Link
                to={`/company-reviews?name=${encodeURIComponent(
                  companyData?.company_profile?.company_name || ""
                )}&id=${companyData?.company_profile?.company_id || ""}`}
                className="cta-link link-reset"
                style={{ fontWeight: "bold" }}
                title="View company ratings"
                aria-label="View company ratings"
              >
                {companyData?.company_profile?.all_review_count}
                <span
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  {" "}
                  ratings
                </span>
              </Link>
            </p>

            <div>
              <div className="d-flex align-items-center pt-2">
                {companyData?.company_profile?.company_logo_url && (
                  <>
                    <Link
                      style={{ textDecoration: "none" }}
                      to={`/company-profile?name=${encodeURIComponent(
                        companyData?.company_profile?.company_name || ""
                      )}&id=${companyData?.company_profile?.company_id || ""}`}
                    >
                      <img
                        src={companyData.company_profile.company_logo_url}
                        alt={`${companyData.company_profile.company_name} Logo`}
                        style={{
                          width: "55px",
                          height: "55px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          marginRight: "12px",
                        }}
                      />
                    </Link>
                  </>
                )}
                <div>
                  <Link
                    style={{ textDecoration: "none" }}
                    to={`/company-profile?name=${encodeURIComponent(
                      companyData?.company_profile?.company_name || ""
                    )}&id=${companyData?.company_profile?.company_id || ""}`}
                  >
                    <h3 className="company_management_h3 mb-1">
                      {companyData?.company_profile?.company_name}
                    </h3>
                  </Link>
                  <h6 className="company_management_p ps-2">Management</h6>
                </div>
              </div>
            </div>

            <div className="">
              <p className="company_profile_rating_p pt-3 mb-0">
                Work at {companyData?.company_profile?.company_name}?
              </p>
              <p className="company_profile_rating_p">
                Rate your manager{" "}
                <span className="text-black fw-bold">anonymously!</span>
              </p>
            </div>

            <div className="d-flex align-items-center">
              <Link to="/add-review" className="">
                <button className="rate_company_profile_btn">
                  Rate
                  <MdOutlineChevronRight className="ms-1 fs-5" />
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Search Bar - Outside the table card */}
        <div className="row pt-lg-5 pt-md-4 pt-3">
          <div className="col-lg-8 col-md-8 col-12">
            <div className="mb-4">
              <h3 className="company_rank_h3 mb-3">Search Managers</h3>
              <div
                className="vam-search-container"
                style={{ width: "100%", position: 'relative' }}
              >
                <input
                  type="text"
                  className="vam-search-input"
                  placeholder={`Search managers in ${
                    companyData?.company_profile?.company_name || "this company"
                  }...`}
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
                <FaSearch
                  style={{
                    position: "absolute",
                    right: "15px",
                    top: "20px",
                    transform: "translateY(-50%)",
                    color: "#666",
                    pointerEvents: "none",
                  }}
                />
                {isSearching && (
                  <div
                    className="position-absolute top-50 end-0 translate-middle-y"
                    style={{ right: "35px" }}
                  >
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {/* Search Dropdown */}
                {searchQuery.trim() && showDropdown && (
                  <div className="vam-search-dropdown-container">
                    <div className="vam-search-dropdown">
                      <div className="vam-search-suggestions-scrollable">
                        {isLoadingSuggestions ? (
                          <div className="vam-search-suggestion vam-loading-suggestion">
                            <div className="d-flex align-items-center justify-content-center">
                              <div
                                className="spinner-border spinner-border-sm text-primary me-2"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              <p className="mb-0 text-muted">
                                Searching managers...
                              </p>
                            </div>
                          </div>
                        ) : searchSuggestions.length > 0 ? (
                          searchSuggestions.map((manager, index) => (
                            <div
                              key={index}
                              className="vam-search-suggestion"
                              onClick={() => handleSuggestionClick(manager)}
                              onMouseDown={(e) => e.preventDefault()}
                              tabIndex={0}
                            >
                              <div className="d-flex align-items-center">
                                <div className="vam-suggestion-avatar me-3">
                                  {companyData?.company_profile
                                    ?.company_logo_url && (
                                    <img
                                      src={
                                        companyData.company_profile
                                          .company_logo_url
                                      }
                                      alt="Company Logo"
                                      style={{ objectFit: "cover" }}
                                    />
                                  )}
                                </div>
                                <div className="flex-grow-1">
                                  <p className="vam-suggestion-name mb-1">
                                    {manager.manager_full_name}
                                  </p>
                                  <p className="vam-suggestion-position mb-1">
                                    {manager.position}
                                  </p>
                                  <div className="vam-suggestion-company-location">
                                    <p className="vam-suggestion-company mb-0">
                                      {
                                        companyData?.company_profile
                                          ?.company_name
                                      }
                                    </p>
                                    <p className="vam-suggestion-location mb-0">
                                      {manager.location ||
                                        "Location not available"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="vam-search-suggestion vam-no-results">
                            <p className="text-center mb-0 text-muted">
                              No managers found for "{searchQuery}"
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="vam-search-dropdown-footer">
                        {suggestionsHasMore && (
                          <div
                            className=""
                            onMouseDown={(e) => e.preventDefault()}
                            tabIndex={0}
                          >
                            <p
                              className="text-center mb-0 text-primary fw-bold mt-2 cursor-pointer"
                              onClick={handleSuggestionsViewMore}
                            >
                              View More
                            </p>
                          </div>
                        )}
                        <div
                          className=""
                          onMouseDown={(e) => e.preventDefault()}
                          tabIndex={0}
                        >
                          <p
                            className="text-center mb-0 text-primary fw-bold mt-3 mb-2 cursor-pointer"
                            onClick={() => {
                              setSearchSuggestions([]);
                              setSearchQuery("");
                              setShowDropdown(false);
                              window.location.href = "/add-manager-profile";
                            }}
                          >
                            Can't find your manager? Create new manager profile
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-8 col-md-8 col-12">
            <div className="company_rank_box">
              <div className="py-3 px-3">
                <h3 className="company_rank_h3 pt-2">All Managers</h3>
                <div className="px-lg-5 pt-2">
                  <div id="companyparentid">
                    <div className="table-responsive">
                      <table>
                        <thead>
                          <tr className="light-text">
                            <td>name</td>
                            <td>position</td>
                            <td>rating</td>
                            <td className="review-col"># of reviews</td>
                            <td>linkedin</td>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedManager ? searchResults : managers).map(
                            (manager, index) => (
                              <tr
                                className={
                                  index % 2 === 0 ? "dark-text" : "dark-text"
                                }
                                key={index}
                              >
                                <td>
                                  <Link
                                    className="table-link"
                                    to={`/manager?name=${encodeURIComponent(
                                      manager.manager_full_name
                                    )}&id=${manager.manager_id}`}
                                    title={`View ${manager.manager_full_name}'s profile`}
                                    aria-label={`View ${manager.manager_full_name}'s profile`}
                                  >
                                    {manager.manager_full_name}
                                  </Link>
                                </td>
                                <td>{manager.position}</td>
                                <td>{manager.rating}</td>
                                <td className="review-col">
                                  {manager.review_count}
                                </td>
                                <td>
                                  {manager.linkedin_profile && (
                                    <Link
                                      to={manager.linkedin_profile}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="icon-bump"
                                      title="Open LinkedIn profile"
                                      aria-label="Open LinkedIn profile"
                                    >
                                      <Icon
                                        path={mdiLinkedin}
                                        size={1.1}
                                        color="#000000"
                                      />
                                    </Link>
                                  )}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <>
                  {/* View More Button for Search Results or All Managers */}
                  {(selectedManager ? searchHasMore : hasMore) && (
                    <div style={{ marginTop: "10px", textAlign: "center" }}>
                      <button
                        onClick={
                          selectedManager ? handleSearchMore : handleViewMore
                        }
                        className="view_more_btn"
                      >
                        View More
                      </button>
                    </div>
                  )}

                  {/* Can't find your manager button */}
                  {selectedManager &&
                    searchResults.length === 0 &&
                    !isSearching &&
                    searchQuery.trim() && (
                      <div style={{ marginTop: "20px", textAlign: "center" }}>
                        <p
                          className="mb-3"
                          style={{ color: "#666", fontSize: "16px" }}
                        >
                          Can't find your manager?
                        </p>
                        <Link
                          to="/add-manager"
                          className="text-decoration-none"
                        >
                          <button
                            className="btn btn-outline-primary"
                            style={{
                              padding: "10px 20px",
                              borderRadius: "8px",
                              border: "2px solid #007bff",
                              backgroundColor: "transparent",
                              color: "#007bff",
                              fontSize: "16px",
                              fontWeight: "500",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#007bff";
                              e.target.style.color = "white";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "transparent";
                              e.target.style.color = "#007bff";
                            }}
                          >
                            Create a new profile...
                          </button>
                        </Link>
                      </div>
                    )}

                  {/* Show message when no search results */}
                  {selectedManager &&
                    searchResults.length === 0 &&
                    !isSearching &&
                    searchQuery.trim() && (
                      <div style={{ marginTop: "10px", textAlign: "center" }}>
                        <p style={{ color: "#666", fontSize: "14px" }}>
                          No managers found matching "{searchQuery}"
                        </p>
                      </div>
                    )}
                </>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateCompanyFooter />
    </div>
  );
};

export default ViewAllManagers;